import React from 'react';
import { 
  EnvelopeIcon, 
  PaperAirplaneIcon, 
  DocumentTextIcon,
  TrashIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { Email, EmailFilters } from '../types/Email';
import { emailApi } from '../services/emailApi';
import PermissionGate from './PermissionGate';

interface EmailListProps {
  emails: Email[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: EmailFilters;
  onFilterChange: (filters: Partial<EmailFilters>) => void;
  onPageChange: (page: number) => void;
  onSort: (sortBy: string) => void;
  onViewEmail: (email: Email) => void;
  onDeleteEmail: (emailId: number) => void;
  onEditDraft?: (email: Email) => void;
  onSendDraft?: (email: Email) => void;
}

const EmailList: React.FC<EmailListProps> = ({
  emails,
  loading,
  error,
  pagination,
  filters,
  onFilterChange,
  onPageChange,
  onSort,
  onViewEmail,
  onDeleteEmail,
  onEditDraft,
  onSendDraft
}) => {
  const handleDelete = async (emailId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this email?')) {
      try {
        await emailApi.deleteEmail(emailId);
        onDeleteEmail(emailId);
      } catch (error) {
        console.error('Error deleting email:', error);
        alert('Failed to delete email');
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <PaperAirplaneIcon className="h-4 w-4 text-green-500" />;
      case 'draft':
        return <DocumentTextIcon className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <EnvelopeIcon className="h-4 w-4 text-red-500" />;
      default:
        return <EnvelopeIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const SortButton: React.FC<{ 
    sortBy: string; 
    children: React.ReactNode; 
    className?: string;
  }> = ({ sortBy, children, className = '' }) => (
    <button
      onClick={() => onSort(sortBy)}
      className={`flex items-center space-x-1 ${className}`}
    >
      <span>{children}</span>
      {filters.sortBy === sortBy && (
        filters.sortOrder === 'ASC' ? 
          <ChevronUpIcon className="h-4 w-4" /> : 
          <ChevronDownIcon className="h-4 w-4" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Loading emails...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="p-8 text-center">
        <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No emails found</h3>
        <p className="text-gray-500">Get started by composing your first email.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <select
              value={filters.status || ''}
              onChange={(e) => onFilterChange({ status: e.target.value || undefined })}
              className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">All Status</option>
              <option value="sent">Sent</option>
              <option value="draft">Draft</option>
              <option value="failed">Failed</option>
            </select>

            <input
              type="text"
              placeholder="Search emails..."
              value={filters.search || ''}
              onChange={(e) => onFilterChange({ search: e.target.value })}
              className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="text-sm text-gray-700">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)} of{' '}
            {pagination.totalRecords} results
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton sortBy="status">Status</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton sortBy="subject">Subject</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recipients
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton sortBy="created_at">Date</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {emails.map((email) => (
              <tr
                key={email.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onViewEmail(email)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {getStatusIcon(email.status)}
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email.status)}`}>
                      {email.status.charAt(0).toUpperCase() + email.status.slice(1)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                    {email.subject || '(No Subject)'}
                  </div>
                  <div className="text-sm text-gray-500 truncate max-w-xs">
                    {email.body.substring(0, 100)}...
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {email.toEmails.slice(0, 2).join(', ')}
                    {email.toEmails.length > 2 && ` +${email.toEmails.length - 2} more`}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(email.sentAt || email.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    {email.status === 'draft' ? (
                      <>
                        <PermissionGate module="emails" permission="edit">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditDraft?.(email);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Draft"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate module="emails" permission="create">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSendDraft?.(email);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Send Draft"
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewEmail(email);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Email"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    )}
                    <PermissionGate module="emails" permission="delete">
                      <button
                        onClick={(e) => handleDelete(email.id, e)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Email"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailList;
